import { blockers } from "hooks/useBlocker"
import { ReactNode, useEffect, useRef } from "react"
import { useBlocker } from "react-router"

interface Props {
  children: ReactNode
}

/**
 * A component that blocks navigation globally.
 *
 * This component must be used once and only once in the app in order for the useBlocker hook to work. It will listen for navigation events and block them if any component has an active blocker.
 */
export const BlockerWrapper = ({ children }: Props) => {
  const blockWithoutConfirmation = useRef(false)
  const confirmationMessages = useRef(new Set<string>())

  const blocker = useBlocker(({ nextLocation }) => {
    blockWithoutConfirmation.current = false
    confirmationMessages.current.clear()

    for (const blocker of blockers.values()) {
      const result = blocker(nextLocation)

      // Block without a confirmation message (takes priority over a confirmation message).
      if (result === true) {
        blockWithoutConfirmation.current = true
        confirmationMessages.current.clear()
        return true
      }
      // Collect confirmation messages from blockers
      if (typeof result === "string" && result.length > 0) {
        confirmationMessages.current.add(result)
      }
    }

    return confirmationMessages.current.size > 0
  })

  // See https://github.com/remix-run/react-router/blob/react-router-dom%407.13.0/packages/react-router/lib/dom/lib.tsx
  // for the reference implementation.
  useEffect(() => {
    // Show a confirmation dialog when we have blocked with a message.
    if (blocker.state === "blocked" && confirmationMessages.current.size > 0) {
      const message = Array.from(confirmationMessages.current).join("\n")
      const proceed = window.confirm(message)
      if (proceed) {
        // This timeout is needed to avoid a weird "race" on POP navigations
        // between the `window.history` revert navigation and the result of
        // `window.confirm`
        setTimeout(blocker.proceed, 0)
      } else {
        blocker.reset()
      }
    }
  }, [blocker])

  // This useEffect is copied from the reference implementation in react-router. It resets the blocker if we are blocked
  // but no longer need to be, though in practice I'm not certain it will ever be the case. Keeping for stability.
  const when = confirmationMessages.current.size > 0 || blockWithoutConfirmation.current
  useEffect(() => {
    if (blocker.state === "blocked" && !when) {
      blocker.reset()
    }
  }, [blocker, when])

  return <>{children}</>
}
